import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import type { RootState, AppDispatch } from '../store';
import { useLiveSession } from '../hooks/useLiveSession';
import { useLiveRecorder } from '../hooks/useLiveRecorder';
import { resetSession, addMessage } from '../store/slices/liveSessionSlice';
import { getTotalAyahs } from '../store/slices/ayahSlice';
import { fetchAyah, fetchLiveSessionHistory, fetchLiveSessionDetail, renameLiveSession, deleteLiveSession, type LiveSessionHistoryItem, type LiveSessionDetail } from '../api/client';
import type { AyahData } from '../types';
import ConversationLog from '../components/LiveSession/ConversationLog';
import TeacherAvatar from '../components/LiveSession/TeacherAvatar';
import SessionSummaryCard from '../components/LiveSession/SessionSummaryCard';
import clsx from 'clsx';

const SURAHS = [
  { number: 1, name: 'Al-Fatiha' }, { number: 2, name: 'Al-Baqarah' },
  { number: 3, name: 'Aal-Imran' }, { number: 4, name: 'An-Nisa' },
  { number: 5, name: 'Al-Maidah' }, { number: 6, name: 'Al-Anam' },
  { number: 7, name: 'Al-Araf' }, { number: 8, name: 'Al-Anfal' },
  { number: 9, name: 'At-Tawbah' }, { number: 10, name: 'Yunus' },
  { number: 11, name: 'Hud' }, { number: 12, name: 'Yusuf' },
  { number: 13, name: 'Ar-Rad' }, { number: 14, name: 'Ibrahim' },
  { number: 15, name: 'Al-Hijr' }, { number: 16, name: 'An-Nahl' },
  { number: 17, name: 'Al-Isra' }, { number: 18, name: 'Al-Kahf' },
  { number: 19, name: 'Maryam' }, { number: 20, name: 'Ta-Ha' },
  { number: 21, name: 'Al-Anbiya' }, { number: 22, name: 'Al-Hajj' },
  { number: 23, name: 'Al-Muminun' }, { number: 24, name: 'An-Nur' },
  { number: 25, name: 'Al-Furqan' }, { number: 26, name: 'Ash-Shuara' },
  { number: 27, name: 'An-Naml' }, { number: 28, name: 'Al-Qasas' },
  { number: 29, name: 'Al-Ankabut' }, { number: 30, name: 'Ar-Rum' },
  { number: 31, name: 'Luqman' }, { number: 32, name: 'As-Sajdah' },
  { number: 33, name: 'Al-Ahzab' }, { number: 34, name: 'Saba' },
  { number: 35, name: 'Fatir' }, { number: 36, name: 'Ya-Sin' },
  { number: 37, name: 'As-Saffat' }, { number: 38, name: 'Sad' },
  { number: 39, name: 'Az-Zumar' }, { number: 40, name: 'Ghafir' },
  { number: 41, name: 'Fussilat' }, { number: 42, name: 'Ash-Shura' },
  { number: 43, name: 'Az-Zukhruf' }, { number: 44, name: 'Ad-Dukhan' },
  { number: 45, name: 'Al-Jathiyah' }, { number: 46, name: 'Al-Ahqaf' },
  { number: 47, name: 'Muhammad' }, { number: 48, name: 'Al-Fath' },
  { number: 49, name: 'Al-Hujurat' }, { number: 50, name: 'Qaf' },
  { number: 51, name: 'Adh-Dhariyat' }, { number: 52, name: 'At-Tur' },
  { number: 53, name: 'An-Najm' }, { number: 54, name: 'Al-Qamar' },
  { number: 55, name: 'Ar-Rahman' }, { number: 56, name: 'Al-Waqiah' },
  { number: 57, name: 'Al-Hadid' }, { number: 58, name: 'Al-Mujadila' },
  { number: 59, name: 'Al-Hashr' }, { number: 60, name: 'Al-Mumtahanah' },
  { number: 61, name: 'As-Saff' }, { number: 62, name: 'Al-Jumuah' },
  { number: 63, name: 'Al-Munafiqun' }, { number: 64, name: 'At-Taghabun' },
  { number: 65, name: 'At-Talaq' }, { number: 66, name: 'At-Tahrim' },
  { number: 67, name: 'Al-Mulk' }, { number: 68, name: 'Al-Qalam' },
  { number: 69, name: 'Al-Haqqah' }, { number: 70, name: 'Al-Maarij' },
  { number: 71, name: 'Nuh' }, { number: 72, name: 'Al-Jinn' },
  { number: 73, name: 'Al-Muzzammil' }, { number: 74, name: 'Al-Muddaththir' },
  { number: 75, name: 'Al-Qiyamah' }, { number: 76, name: 'Al-Insan' },
  { number: 77, name: 'Al-Mursalat' }, { number: 78, name: 'An-Naba' },
  { number: 79, name: 'An-Naziat' }, { number: 80, name: 'Abasa' },
  { number: 81, name: 'At-Takwir' }, { number: 82, name: 'Al-Infitar' },
  { number: 83, name: 'Al-Mutaffifin' }, { number: 84, name: 'Al-Inshiqaq' },
  { number: 85, name: 'Al-Buruj' }, { number: 86, name: 'At-Tariq' },
  { number: 87, name: 'Al-Ala' }, { number: 88, name: 'Al-Ghashiyah' },
  { number: 89, name: 'Al-Fajr' }, { number: 90, name: 'Al-Balad' },
  { number: 91, name: 'Ash-Shams' }, { number: 92, name: 'Al-Layl' },
  { number: 93, name: 'Ad-Duha' }, { number: 94, name: 'Ash-Sharh' },
  { number: 95, name: 'At-Tin' }, { number: 96, name: 'Al-Alaq' },
  { number: 97, name: 'Al-Qadr' }, { number: 98, name: 'Al-Bayyinah' },
  { number: 99, name: 'Az-Zalzalah' }, { number: 100, name: 'Al-Adiyat' },
  { number: 101, name: 'Al-Qariah' }, { number: 102, name: 'At-Takathur' },
  { number: 103, name: 'Al-Asr' }, { number: 104, name: 'Al-Humazah' },
  { number: 105, name: 'Al-Fil' }, { number: 106, name: 'Quraysh' },
  { number: 107, name: 'Al-Maun' }, { number: 108, name: 'Al-Kawthar' },
  { number: 109, name: 'Al-Kafirun' }, { number: 110, name: 'An-Nasr' },
  { number: 111, name: 'Al-Masad' }, { number: 112, name: 'Al-Ikhlas' },
  { number: 113, name: 'Al-Falaq' }, { number: 114, name: 'An-Nas' },
];

const LANGUAGES = [
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'ar', label: 'العربية', flag: '🇸🇦' },
  { code: 'ur', label: 'اردو', flag: '🇵🇰' },
  { code: 'ms', label: 'Melayu', flag: '🇲🇾' },
  { code: 'tr', label: 'Türkçe', flag: '🇹🇷' },
  { code: 'fr', label: 'Français', flag: '🇫🇷' },
  { code: 'id', label: 'Indonesia', flag: '🇮🇩' },
  { code: 'bn', label: 'বাংলা', flag: '🇧🇩' },
  { code: 'es', label: 'Español', flag: '🇪🇸' },
];

const RECITERS = [
  { id: 'husary', name: 'Al-Husary', folder: 'Husary_128kbps' },
  { id: 'minshawi', name: 'Al-Minshawi', folder: 'Minshawy_Murattal_128kbps' },
  { id: 'abdulbasit', name: 'Abdul Basit', folder: 'Abdul_Basit_Murattal_192kbps' },
];

function buildAudioUrl(folder: string, s: number, a: number): string {
  return `https://everyayah.com/data/${folder}/${String(s).padStart(3, '0')}${String(a).padStart(3, '0')}.mp3`;
}

function surahName(num: number): string {
  return SURAHS.find((s) => s.number === num)?.name || `Surah ${num}`;
}

// ─── i18n strings ───────────────────────────────────────────────
const UI_STRINGS: Record<string, Record<string, string>> = {
  en: {
    title: 'Live Tajweed Session',
    subtitle: 'Practice with Ustadh Noor',
    chooseSurah: 'Choose a Surah',
    searchSurah: 'Search surah...',
    teacherLang: 'Teacher Language',
    startSession: 'Start Session',
    newSession: 'New Session',
    noPastSessions: 'No past sessions',
    connecting: 'Connecting...',
    preparing: 'Ustadh Noor is preparing your lesson...',
    tapToRecite: 'Tap to recite',
    tapToStop: 'Tap to stop',
    sending: 'Sending',
    processing: 'Processing',
    repeat: 'Repeat',
    nextAyah: 'Next Ayah',
    goTo: 'Go to...',
    end: 'End',
    listenFirst: 'Listen',
    playing: 'Playing',
  },
  ar: {
    title: 'جلسة تجويد مباشرة',
    subtitle: 'تمرّن مع الأستاذ نور',
    chooseSurah: 'اختر سورة',
    searchSurah: 'ابحث عن سورة...',
    teacherLang: 'لغة المعلّم',
    startSession: 'ابدأ الجلسة',
    newSession: 'جلسة جديدة',
    noPastSessions: 'لا توجد جلسات سابقة',
    connecting: 'جارٍ الاتصال...',
    preparing: 'الأستاذ نور يحضّر درسك...',
    tapToRecite: 'اضغط للتلاوة',
    tapToStop: 'اضغط للإيقاف',
    sending: 'جارٍ الإرسال',
    processing: 'جارٍ المعالجة',
    repeat: 'أعد',
    nextAyah: 'الآية التالية',
    goTo: 'انتقل إلى...',
    end: 'إنهاء',
    listenFirst: 'استمع',
    playing: 'يعمل',
  },
  ur: {
    title: 'لائیو تجوید سیشن',
    subtitle: 'استاد نور کے ساتھ مشق کریں',
    chooseSurah: 'سورت منتخب کریں',
    searchSurah: 'سورت تلاش کریں...',
    teacherLang: 'استاد کی زبان',
    startSession: 'سیشن شروع کریں',
    newSession: 'نیا سیشن',
    noPastSessions: 'کوئی پچھلا سیشن نہیں',
    connecting: 'رابطہ ہو رہا ہے...',
    preparing: 'استاد نور آپ کا سبق تیار کر رہے ہیں...',
    tapToRecite: 'تلاوت کے لیے ٹیپ کریں',
    tapToStop: 'رکنے کے لیے ٹیپ کریں',
    sending: 'بھیج رہا ہے',
    processing: 'عمل ہو رہا ہے',
    repeat: 'دوبارہ',
    nextAyah: 'اگلی آیت',
    goTo: 'جائیں...',
    end: 'ختم',
    listenFirst: 'سنیں',
    playing: 'چل رہا ہے',
  },
  tr: {
    title: 'Canlı Tecvid Oturumu',
    subtitle: 'Üstad Noor ile pratik yapın',
    chooseSurah: 'Sure Seçin',
    searchSurah: 'Sure ara...',
    teacherLang: 'Öğretmen Dili',
    startSession: 'Oturumu Başlat',
    newSession: 'Yeni Oturum',
    noPastSessions: 'Geçmiş oturum yok',
    connecting: 'Bağlanıyor...',
    preparing: 'Üstad Noor dersinizi hazırlıyor...',
    tapToRecite: 'Okumak için dokunun',
    tapToStop: 'Durdurmak için dokunun',
    sending: 'Gönderiliyor',
    processing: 'İşleniyor',
    repeat: 'Tekrar',
    nextAyah: 'Sonraki Ayet',
    goTo: 'Git...',
    end: 'Bitir',
    listenFirst: 'Dinle',
    playing: 'Çalıyor',
  },
  fr: {
    title: 'Session Tajweed en Direct',
    subtitle: 'Pratiquez avec Ustadh Noor',
    chooseSurah: 'Choisir une Sourate',
    searchSurah: 'Chercher une sourate...',
    teacherLang: 'Langue du professeur',
    startSession: 'Commencer',
    newSession: 'Nouvelle Session',
    noPastSessions: 'Aucune session',
    connecting: 'Connexion...',
    preparing: 'Ustadh Noor prépare votre leçon...',
    tapToRecite: 'Appuyez pour réciter',
    tapToStop: 'Appuyez pour arrêter',
    sending: 'Envoi',
    processing: 'Traitement',
    repeat: 'Répéter',
    nextAyah: 'Verset suivant',
    goTo: 'Aller à...',
    end: 'Fin',
    listenFirst: 'Écouter',
    playing: 'Lecture',
  },
};

// ─── Ayah Picker Modal ──────────────────────────────────────────
function AyahPickerModal({
  surah,
  currentAyah,
  totalAyahs,
  onSelect,
  onClose,
}: {
  surah: number;
  currentAyah: number;
  totalAyahs: number;
  onSelect: (surah: number, ayah: number) => void;
  onClose: () => void;
}) {
  const [pickerSurah, setPickerSurah] = useState(surah);
  const [pickerTotal, setPickerTotal] = useState(totalAyahs);
  const [ayahTexts, setAyahTexts] = useState<Record<number, string>>({});
  const [loadedCount, setLoadedCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [surahSearch, setSurahSearch] = useState('');
  const listRef = useRef<HTMLDivElement>(null);

  // Lock body scroll
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  // Load ayah texts — first batch of 20, then load more as user scrolls
  useEffect(() => {
    const total = getTotalAyahs(pickerSurah);
    setPickerTotal(total);
    setAyahTexts({});
    setLoadedCount(0);
    loadBatch(pickerSurah, 1, Math.min(20, total));
  }, [pickerSurah]);

  const loadBatch = async (s: number, from: number, to: number) => {
    setLoading(true);
    const batch = Array.from({ length: to - from + 1 }, (_, i) => from + i);
    const results = await Promise.allSettled(batch.map((a) => fetchAyah(s, a)));
    const newTexts: Record<number, string> = {};
    for (let i = 0; i < results.length; i++) {
      const r = results[i];
      if (r.status === 'fulfilled' && r.value) {
        newTexts[batch[i]] = r.value.referenceTextNormalized || r.value.sourceTextRaw || '';
      }
    }
    setAyahTexts((prev) => ({ ...prev, ...newTexts }));
    setLoadedCount(to);
    setLoading(false);
  };

  // Load more when scrolling near bottom
  const handleScroll = useCallback(() => {
    const el = listRef.current;
    if (!el || loading) return;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 200;
    if (nearBottom && loadedCount < pickerTotal) {
      const nextFrom = loadedCount + 1;
      const nextTo = Math.min(loadedCount + 20, pickerTotal);
      loadBatch(pickerSurah, nextFrom, nextTo);
    }
  }, [loading, loadedCount, pickerTotal, pickerSurah]);

  // Scroll to current ayah on initial load
  useEffect(() => {
    if (pickerSurah === surah && listRef.current && loadedCount > 0) {
      setTimeout(() => {
        const el = listRef.current?.querySelector(`[data-ayah="${currentAyah}"]`);
        if (el) el.scrollIntoView({ block: 'center' });
      }, 100);
    }
  }, [pickerSurah, surah, currentAyah, loadedCount]);

  const filteredSurahs = surahSearch
    ? SURAHS.filter((s) => s.name.toLowerCase().includes(surahSearch.toLowerCase()) || String(s.number).includes(surahSearch))
    : SURAHS;

  return (
    <div className="fixed inset-0 z-[200] bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-2xl w-full max-w-lg flex flex-col"
        style={{ height: '80vh' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-100 dark:border-gray-800 shrink-0">
          <h2 className="text-base font-semibold text-gray-900 dark:text-white">Go to Ayah</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Surah selector */}
        <div className="p-3 border-b border-gray-100 dark:border-gray-800 shrink-0">
          <input
            type="text"
            value={surahSearch}
            onChange={(e) => setSurahSearch(e.target.value)}
            placeholder="Search surah..."
            className="w-full px-3 py-1.5 text-sm rounded-lg bg-gray-100 dark:bg-gray-800 border-none text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 mb-2"
          />
          <div className="flex gap-1.5 overflow-x-auto pb-1 hide-scrollbar">
            {filteredSurahs.map((s) => (
              <button
                key={s.number}
                onClick={() => { setPickerSurah(s.number); setSurahSearch(''); }}
                className={clsx(
                  'shrink-0 px-2.5 py-1 rounded-lg text-[12px] transition-colors whitespace-nowrap',
                  pickerSurah === s.number
                    ? 'bg-emerald-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                )}
              >
                {s.number}. {s.name}
              </button>
            ))}
          </div>
        </div>

        {/* Ayah list — scrollable, paginated */}
        <div
          ref={listRef}
          className="min-h-0 flex-1 overflow-y-auto"
          onScroll={handleScroll}
        >
          {loadedCount === 0 && loading ? (
            <div className="flex items-center justify-center py-12">
              <svg className="animate-spin h-6 w-6 text-emerald-500" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            </div>
          ) : (
            <>
              <div className="divide-y divide-gray-100 dark:divide-gray-800/50">
                {Array.from({ length: Math.min(loadedCount, pickerTotal) }, (_, i) => i + 1).map((a) => {
                  const text = ayahTexts[a];
                  const isCurrent = pickerSurah === surah && a === currentAyah;
                  return (
                    <button
                      key={a}
                      data-ayah={a}
                      onClick={() => onSelect(pickerSurah, a)}
                      className={clsx(
                        'w-full text-left px-4 py-3 transition-colors flex gap-3 items-start',
                        isCurrent
                          ? 'bg-emerald-50 dark:bg-emerald-500/10'
                          : 'hover:bg-gray-50 dark:hover:bg-gray-800/50'
                      )}
                    >
                      <span className={clsx(
                        'text-xs font-medium mt-1 w-6 text-center shrink-0',
                        isCurrent ? 'text-emerald-600 dark:text-emerald-400' : 'text-gray-400 dark:text-gray-600'
                      )}>
                        {a}
                      </span>
                      <span className={clsx(
                        'font-arabic text-base leading-relaxed',
                        isCurrent ? 'text-emerald-700 dark:text-emerald-300' : 'text-gray-800 dark:text-gray-200',
                        !text && 'text-gray-300 dark:text-gray-700 italic text-sm'
                      )} dir="rtl">
                        {text || '...'}
                      </span>
                    </button>
                  );
                })}
              </div>
              {/* Load more indicator */}
              {loadedCount < pickerTotal && (
                <div className="flex items-center justify-center py-4">
                  {loading ? (
                    <svg className="animate-spin h-5 w-5 text-emerald-500" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                  ) : (
                    <span className="text-xs text-gray-400">{loadedCount} of {pickerTotal} loaded — scroll for more</span>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}


function t(lang: string, key: string): string {
  return UI_STRINGS[lang]?.[key] || UI_STRINGS.en[key] || key;
}

// ─── Sidebar ────────────────────────────────────────────────────
function Sidebar({
  history,
  activeSessionId,
  activeSessionLabel,
  onSelect,
  onNew,
  onRename,
  onDelete,
  viewingSessionId,
  sidebarOpen,
  onToggle,
  lang,
}: {
  history: LiveSessionHistoryItem[];
  activeSessionId: string | null;
  activeSessionLabel: string | null;
  viewingSessionId: string | null;
  onSelect: (sessionId: string) => void;
  onNew: () => void;
  onRename: (sessionId: string, title: string) => void;
  onDelete: (sessionId: string) => void;
  sidebarOpen: boolean;
  onToggle: () => void;
  lang: string;
}) {
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const editRef = useRef<HTMLInputElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Focus input when editing starts
  useEffect(() => {
    if (editingId && editRef.current) editRef.current.focus();
  }, [editingId]);

  // Close menu on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpenId(null);
    };
    if (menuOpenId) {
      document.addEventListener('mousedown', handler);
      return () => document.removeEventListener('mousedown', handler);
    } else {
      setConfirmDeleteId(null);
    }
  }, [menuOpenId]);

  const commitRename = () => {
    if (editingId && editValue.trim()) onRename(editingId, editValue.trim());
    setEditingId(null);
  };

  // Check if active session is already in history (i.e. it ended and history re-fetched)
  const activeInHistory = activeSessionId ? history.some((h) => h.sessionId === activeSessionId) : false;

  return (
    <>
      <button
        onClick={onToggle}
        className="lg:hidden fixed top-[4.5rem] left-3 z-40 p-1.5 rounded-lg bg-white/90 dark:bg-gray-950/90 backdrop-blur border border-gray-200/80 dark:border-gray-800/80"
      >
        <svg className="w-4 h-4 text-gray-500 dark:text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          {sidebarOpen
            ? <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            : <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25H12" />}
        </svg>
      </button>

      {sidebarOpen && <div className="lg:hidden fixed inset-0 z-30 bg-black/15" onClick={onToggle} />}

      <aside className={clsx(
        'fixed top-16 left-0 z-40 h-[calc(100vh-4rem)] w-56 flex flex-col',
        'bg-white dark:bg-gray-950 border-r border-gray-200 dark:border-gray-800',
        'transition-transform duration-200 ease-out',
        sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
      )}>
        <div className="p-2.5 shrink-0">
          <button
            onClick={onNew}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-900 text-sm text-gray-600 dark:text-gray-400 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
            </svg>
            {t(lang, 'newSession')}
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-1.5 pb-3">
          {/* Active session pinned at top */}
          {activeSessionId && !activeInHistory && activeSessionLabel && (
            <div className="group relative rounded-lg bg-emerald-50 dark:bg-emerald-500/10 transition-colors mb-0.5">
              {editingId === activeSessionId ? (
                <div className="px-2 py-1.5">
                  <input
                    ref={editRef}
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    onBlur={() => { if (editValue.trim()) onRename(activeSessionId, editValue.trim()); setEditingId(null); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') { if (editValue.trim()) onRename(activeSessionId, editValue.trim()); setEditingId(null); } if (e.key === 'Escape') setEditingId(null); }}
                    className="w-full text-[13px] bg-white dark:bg-gray-800 border border-emerald-500 rounded px-1.5 py-1 text-gray-900 dark:text-gray-100 focus:outline-none"
                    maxLength={100}
                  />
                </div>
              ) : (
                <div className="w-full text-left px-2.5 py-2">
                  <span className="text-[13px] truncate flex items-center gap-1.5 text-emerald-700 dark:text-emerald-400">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0 animate-pulse" />
                    <span className="truncate">{activeSessionLabel}</span>
                  </span>
                  <span className="text-[11px] text-gray-400 dark:text-gray-600">In progress</span>
                </div>
              )}
              {editingId !== activeSessionId && (
                <button
                  onClick={(e) => { e.stopPropagation(); setEditingId(activeSessionId); setEditValue(activeSessionLabel); }}
                  className="absolute right-1.5 top-2 p-1 rounded opacity-0 group-hover:opacity-100 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-800 transition-all"
                  title="Rename"
                >
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L6.832 19.82a4.5 4.5 0 01-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 011.13-1.897L16.863 4.487z" />
                  </svg>
                </button>
              )}
            </div>
          )}
          {history.map((h, idx) => {
            const defaultName = `${surahName(h.surah)} ${h.surah}:${h.ayahStart}`;
            const displayName = h.title || defaultName;
            const score = h.averageScore != null ? Math.round(h.averageScore) : null;
            const isEditing = editingId === h.sessionId;
            const isMenuOpen = menuOpenId === h.sessionId;

            const isActive = h.sessionId === activeSessionId;
            const isViewing = h.sessionId === viewingSessionId;

            return (
              <div
                key={h.sessionId || idx}
                className={clsx(
                  'group relative rounded-lg transition-colors mb-0.5',
                  isActive ? 'bg-emerald-50 dark:bg-emerald-500/10'
                    : isViewing ? 'bg-gray-100 dark:bg-gray-800'
                    : 'hover:bg-gray-100 dark:hover:bg-gray-900'
                )}
              >
                {isEditing ? (
                  <div className="px-2 py-1.5">
                    <input
                      ref={editRef}
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      onBlur={commitRename}
                      onKeyDown={(e) => { if (e.key === 'Enter') commitRename(); if (e.key === 'Escape') setEditingId(null); }}
                      className="w-full text-[13px] bg-white dark:bg-gray-800 border border-emerald-500 rounded px-1.5 py-1 text-gray-900 dark:text-gray-100 focus:outline-none"
                      maxLength={100}
                    />
                  </div>
                ) : (
                  <button
                    onClick={() => onSelect(h.sessionId)}
                    className="w-full text-left px-2.5 py-2"
                  >
                    <span className={clsx(
                      'text-[13px] truncate flex items-center gap-1.5',
                      isActive
                        ? 'text-emerald-700 dark:text-emerald-400 font-medium'
                        : 'text-gray-700 dark:text-gray-300'
                    )}>
                      {isActive && <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0 animate-pulse" />}
                      <span className="truncate">{displayName}</span>
                    </span>
                    <span className="text-[11px] text-gray-400 dark:text-gray-600">
                      {score != null && (
                        <span className={clsx(
                          'font-medium tabular-nums',
                          score >= 80 ? 'text-emerald-500' : score >= 60 ? 'text-amber-500' : 'text-red-400'
                        )}>{score}/100</span>
                      )}
                      {score != null && ' · '}
                      {h.totalTurns} turn{h.totalTurns !== 1 ? 's' : ''}
                      {h.createdAt && ` · ${new Date(h.createdAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`}
                    </span>
                  </button>
                )}

                {/* 3-dot menu trigger */}
                {!isEditing && (
                  <button
                    onClick={(e) => { e.stopPropagation(); setMenuOpenId(isMenuOpen ? null : h.sessionId); }}
                    className="absolute right-1.5 top-2 p-1 rounded opacity-0 group-hover:opacity-100 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-800 transition-all"
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <circle cx="4" cy="10" r="2" />
                      <circle cx="10" cy="10" r="2" />
                      <circle cx="16" cy="10" r="2" />
                    </svg>
                  </button>
                )}

                {/* Context menu */}
                {isMenuOpen && (
                  <div
                    ref={menuRef}
                    className="absolute right-0 top-8 z-50 w-36 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg py-1"
                  >
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setMenuOpenId(null);
                        setEditingId(h.sessionId);
                        setEditValue(displayName);
                      }}
                      className="w-full text-left px-3 py-1.5 text-[13px] text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2"
                    >
                      <svg className="w-3.5 h-3.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L6.832 19.82a4.5 4.5 0 01-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 011.13-1.897L16.863 4.487z" />
                      </svg>
                      Rename
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setMenuOpenId(null);
                        const text = `Tajweed Session: ${displayName} — Score: ${score ?? 'N/A'}/100`;
                        navigator.clipboard.writeText(text).catch(() => {});
                      }}
                      className="w-full text-left px-3 py-1.5 text-[13px] text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2"
                    >
                      <svg className="w-3.5 h-3.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M7.217 10.907a2.25 2.25 0 100 2.186m0-2.186c.18.324.283.696.283 1.093s-.103.77-.283 1.093m0-2.186l9.566-5.314m-9.566 7.5l9.566 5.314m0 0a2.25 2.25 0 103.935 2.186 2.25 2.25 0 00-3.935-2.186zm0-12.814a2.25 2.25 0 103.933-2.185 2.25 2.25 0 00-3.933 2.185z" />
                      </svg>
                      Share
                    </button>
                    <div className="mx-2 my-1 border-t border-gray-100 dark:border-gray-700" />
                    {confirmDeleteId === h.sessionId ? (
                      <div className="px-3 py-2">
                        <p className="text-[12px] text-gray-500 dark:text-gray-400 mb-2">Delete this session?</p>
                        <div className="flex gap-1.5">
                          <button
                            onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(null); setMenuOpenId(null); onDelete(h.sessionId); }}
                            className="flex-1 text-[12px] font-medium text-white bg-red-500 hover:bg-red-600 rounded px-2 py-1 transition-colors"
                          >
                            Delete
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(null); }}
                            className="flex-1 text-[12px] text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded px-2 py-1 transition-colors"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button
                        onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(h.sessionId); }}
                        className="w-full text-left px-3 py-1.5 text-[13px] text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 flex items-center gap-2"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                        </svg>
                        Delete
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
          {history.length === 0 && (
            <p className="text-xs text-gray-400 dark:text-gray-600 text-center pt-4">{t(lang, 'noPastSessions')}</p>
          )}
        </div>
      </aside>
    </>
  );
}


// ─── Main ───────────────────────────────────────────────────────
export default function LiveSessionPage() {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { sessionId: urlSessionId } = useParams<{ sessionId?: string }>();
  const {
    status, phase, surah, surahName: sliceSurahName, ayah, ayahText,
    isTeacherSpeaking, processingStep, error, messages, sessionId: activeSessionId,
  } = useSelector((state: RootState) => state.liveSession);

  // If URL has a session ID but we're idle (page reload / HMR), try to load it as a past session
  useEffect(() => {
    if (urlSessionId && status === 'idle' && !pastSession) {
      fetchLiveSessionDetail(urlSessionId)
        .then((detail) => {
          if (detail.messages && detail.messages.length > 0) {
            setPastSession(detail);
          } else {
            navigate('/live-session', { replace: true });
          }
        })
        .catch(() => navigate('/live-session', { replace: true }));
    }
  }, [urlSessionId, status, navigate]);

  // Past session viewing
  const [pastSession, setPastSession] = useState<LiveSessionDetail | null>(null);

  const viewPastSession = useCallback((sessionId: string) => {
    fetchLiveSessionDetail(sessionId)
      .then((detail) => {
        setPastSession(detail);
        navigate(`/live-session/${sessionId}`, { replace: true });
        setSidebarOpen(false);
      })
      .catch(() => {});
  }, [navigate]);

  const closePastSession = () => {
    setPastSession(null);
    navigate('/live-session', { replace: true });
  };

  // Auto-play reference audio callback (used by useLiveSession when teacher says [LISTEN])
  const autoPlayReference = useCallback(() => {
    if (!refAudioRef.current) refAudioRef.current = new Audio();
    const audio = refAudioRef.current;
    // Always play from the current surah/ayah/reciter at time of call
    const state = storeRef.current;
    if (!state.surah || !state.ayah) return;
    const r = RECITERS.find((x) => x.id === state.reciter) || RECITERS[0];
    audio.src = buildAudioUrl(r.folder, state.surah, state.ayah);
    audio.onended = () => setRefPlaying(false);
    audio.onerror = () => setRefPlaying(false);
    audio.play().then(() => setRefPlaying(true)).catch(() => setRefPlaying(false));
  }, []);

  const { connect, send, disconnect } = useLiveSession(autoPlayReference);
  const [selectedSurah, setSelectedSurah] = useState(1);
  const [selectedLanguage, setSelectedLanguage] = useState(() => localStorage.getItem('tajweed_session_lang') || 'en');
  const [surahSearch, setSurahSearch] = useState('');
  const [showAyahPicker, setShowAyahPicker] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [history, setHistory] = useState<LiveSessionHistoryItem[]>([]);
  const [selectedReciter, setSelectedReciter] = useState(RECITERS[0].id);
  const [reciterOpen, setReciterOpen] = useState(false);
  const [renamingActive, setRenamingActive] = useState(false);
  const [activeTitle, setActiveTitle] = useState('');
  const activeRenameRef = useRef<HTMLInputElement>(null);
  // Pending rename: stored locally until session ends and summary row exists
  const [pendingRename, setPendingRename] = useState<{ sessionId: string; title: string } | null>(null);
  const reciterRef = useRef<HTMLDivElement>(null);
  const refAudioRef = useRef<HTMLAudioElement | null>(null);
  const [refPlaying, setRefPlaying] = useState(false);
  const [transitioning, setTransitioning] = useState(false);

  // Clear transitioning when we get a new teacher message (ayah loaded)
  useEffect(() => {
    if (messages.length > 0) setTransitioning(false);
  }, [messages.length]);

  // Stable ref for autoPlayReference callback to read current state without deps
  const storeRef = useRef({ surah: 0, ayah: 0, reciter: RECITERS[0].id });
  storeRef.current = { surah, ayah, reciter: selectedReciter };

  const handleAudioComplete = useCallback((audioB64: string) => {
    send({ type: 'audio_chunk', audio_b64: audioB64 });
  }, [send]);

  const { isRecording, isSubmitting, inputLevel, freqBars, startRecording, stopRecording, resetSubmitting } = useLiveRecorder(handleAudioComplete);
  const [recordingSeconds, setRecordingSeconds] = useState(0);

  useEffect(() => { if (phase === 'processing' || phase === 'feedback' || phase === 'listening') resetSubmitting(); }, [phase, resetSubmitting]);

  // Recording timer
  useEffect(() => {
    if (!isRecording) { setRecordingSeconds(0); return; }
    const t = setInterval(() => setRecordingSeconds((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [isRecording]);
  useEffect(() => { return () => { disconnect(); dispatch(resetSession()); }; }, [disconnect, dispatch]);
  useEffect(() => {
    const h = (e: MouseEvent) => { if (reciterRef.current && !reciterRef.current.contains(e.target as Node)) setReciterOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  // Load session history once on mount
  const historyLoadedRef = useRef(false);
  useEffect(() => {
    if (!historyLoadedRef.current) {
      historyLoadedRef.current = true;
      fetchLiveSessionHistory(20).then(setHistory).catch(() => {});
    }
  }, []);

  // Re-fetch only when a session ends (new entry might exist)
  useEffect(() => {
    if (status === 'ended') {
      fetchLiveSessionHistory(20).then((h) => {
        setHistory(h);
        if (pendingRename && h.some((s) => s.sessionId === pendingRename.sessionId)) {
          renameLiveSession(pendingRename.sessionId, pendingRename.title).then(() => {
            setHistory((prev) => prev.map((s) => s.sessionId === pendingRename.sessionId ? { ...s, title: pendingRename.title } : s));
          }).catch(() => {});
          setPendingRename(null);
        }
      }).catch(() => {});
    }
  }, [status]); // eslint-disable-line react-hooks/exhaustive-deps

  const startSession = useCallback(async (surahNum: number, ayahNum: number = 1) => {
    setPastSession(null);
    if (status === 'active') {
      send({ type: 'user_action', action: 'end_session' });
      disconnect();
      dispatch(resetSession());
      await new Promise((r) => setTimeout(r, 300));
    }
    localStorage.setItem('tajweed_session_lang', selectedLanguage);
    const sessionId = crypto.randomUUID();
    // Update URL to include session ID
    navigate(`/live-session/${sessionId}`, { replace: true });
    await connect(sessionId);
    setTimeout(() => {
      send({ type: 'start_session', surah: surahNum, ayah: ayahNum, user_level: 'intermediate', language: selectedLanguage });
    }, 500);
    setSidebarOpen(false);
  }, [connect, send, disconnect, dispatch, selectedLanguage, status, navigate]);

  const handleNewSession = () => {
    if (status === 'active') send({ type: 'user_action', action: 'end_session' });
    disconnect();
    dispatch(resetSession());
    setPastSession(null);
    navigate('/live-session', { replace: true });
    setSidebarOpen(false);
  };

  const playRef = useCallback(() => {
    if (!surah || !ayah) return;
    const r = RECITERS.find((x) => x.id === selectedReciter) || RECITERS[0];
    if (!refAudioRef.current) refAudioRef.current = new Audio();
    const a = refAudioRef.current;
    if (refPlaying) { a.pause(); setRefPlaying(false); return; }
    a.src = buildAudioUrl(r.folder, surah, ayah);
    a.onended = () => setRefPlaying(false);
    a.onerror = () => setRefPlaying(false);
    a.play().then(() => setRefPlaying(true)).catch(() => setRefPlaying(false));
  }, [surah, ayah, selectedReciter, refPlaying]);

  useEffect(() => { refAudioRef.current?.pause(); setRefPlaying(false); }, [ayah, surah]);

  const filteredSurahs = surahSearch
    ? SURAHS.filter((s) => s.name.toLowerCase().includes(surahSearch.toLowerCase()) || String(s.number).includes(surahSearch))
    : SURAHS;

  // ─── Render ───────────────────────────────────────────────────
  return (
    <div className="flex min-h-[calc(100vh-4rem)]">
      {/* Sidebar — always present, fixed position */}
      <Sidebar
        history={history}
        activeSessionId={activeSessionId}
        activeSessionLabel={status === 'active' || status === 'connecting' ? (pendingRename?.sessionId === activeSessionId ? pendingRename.title : `${surahName(surah)} ${surah}:${ayah}`) : null}
        viewingSessionId={pastSession?.sessionId || null}
        onSelect={(sessionId) => viewPastSession(sessionId)}
        onNew={handleNewSession}
        onRename={(sessionId, title) => {
          renameLiveSession(sessionId, title).then(() => {
            setHistory((prev) => prev.map((h) => h.sessionId === sessionId ? { ...h, title } : h));
          }).catch(() => {});
        }}
        onDelete={(sessionId) => {
          deleteLiveSession(sessionId).then(() => {
            setHistory((prev) => prev.filter((h) => h.sessionId !== sessionId));
          }).catch(() => {});
        }}
        sidebarOpen={sidebarOpen}
        onToggle={() => setSidebarOpen(!sidebarOpen)}
        lang={selectedLanguage}
      />

      {/* Main content — offset by sidebar width on desktop */}
      <main className="flex-1 min-w-0 lg:ml-56">

        {/* ── IDLE ── */}
        {/* ── VIEWING PAST SESSION ── */}
        {pastSession && status === 'idle' && (
          <div className="max-w-2xl mx-auto px-4 py-6 flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-base font-semibold text-gray-900 dark:text-white">
                  {pastSession.title || `${surahName(pastSession.surah)} ${pastSession.surah}:${pastSession.ayahStart}`}
                </h1>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {pastSession.totalTurns} turn{pastSession.totalTurns !== 1 ? 's' : ''}
                  {pastSession.averageScore != null && ` · Score: ${Math.round(pastSession.averageScore)}/100`}
                  {pastSession.createdAt && ` · ${new Date(pastSession.createdAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}`}
                </p>
              </div>
              <button onClick={closePastSession} className="text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-1.5 transition-colors">
                Close
              </button>
            </div>

            {/* Past messages */}
            <div className="flex flex-col gap-3 max-h-[60vh] overflow-y-auto">
              {pastSession.messages.map((msg, idx) => (
                <div key={idx} className={clsx('flex', msg.role === 'teacher' ? 'justify-start' : 'justify-end')}>
                  <div className={clsx(
                    'max-w-[85%] rounded-2xl px-4 py-3 text-sm',
                    msg.role === 'teacher'
                      ? 'bg-emerald-50 dark:bg-emerald-500/10 text-gray-700 dark:text-gray-200 rounded-tl-sm'
                      : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200 rounded-tr-sm'
                  )}>
                    {msg.role === 'teacher' && (
                      <p className="text-xs font-semibold text-emerald-600 dark:text-emerald-400 mb-1">Ustadh Noor</p>
                    )}
                    <p>{msg.text}</p>
                    {msg.score != null && (
                      <p className={clsx(
                        'mt-2 text-xs font-semibold',
                        msg.score >= 80 ? 'text-emerald-600 dark:text-emerald-400' :
                        msg.score >= 60 ? 'text-yellow-600 dark:text-yellow-400' :
                        'text-red-500 dark:text-red-400'
                      )}>Score: {Math.round(msg.score)}/100</p>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Action buttons */}
            <div className="flex justify-center gap-3 pt-2">
              <button
                onClick={() => {
                  // Continue: pre-load past messages into Redux, then start new session on same ayah
                  const pastMsgs = pastSession.messages;
                  closePastSession();
                  startSession(pastSession.surah, pastSession.ayahStart).then(() => {
                    // Inject past messages into the conversation after connection
                    for (const msg of pastMsgs) {
                      dispatch(addMessage({
                        role: msg.role as 'teacher' | 'student',
                        text: msg.text,
                        score: msg.score,
                        timestamp: Date.now(),
                      }));
                    }
                  });
                }}
                className="btn-primary text-sm px-6 py-2"
              >
                {t(selectedLanguage, 'repeat')} & Continue
              </button>
              <button
                onClick={() => { closePastSession(); startSession(pastSession.surah, pastSession.ayahStart); }}
                className="btn-secondary text-sm px-6 py-2"
              >
                Start Fresh
              </button>
            </div>
          </div>
        )}

        {/* ── IDLE ── */}
        {status === 'idle' && !pastSession && (
          <div className="max-w-md mx-auto px-4 py-10">
            <div className="text-center mb-8">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center mx-auto mb-4 text-white text-2xl font-bold font-arabic">ت</div>
              <h1 className="text-xl font-bold text-gray-900 dark:text-white mb-1">{t(selectedLanguage, 'title')}</h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">{t(selectedLanguage, 'subtitle')}</p>
            </div>

            {/* Surah */}
            <div className="mb-5">
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2 block">Surah</label>
              <input
                type="text"
                value={surahSearch}
                onChange={(e) => setSurahSearch(e.target.value)}
                placeholder="Search..."
                className="w-full px-3 py-2 text-sm rounded-lg bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent mb-2"
              />
              <div className="grid grid-cols-2 gap-1.5 max-h-44 overflow-y-auto rounded-lg">
                {filteredSurahs.map((s) => (
                  <button
                    key={s.number}
                    onClick={() => { setSelectedSurah(s.number); setSurahSearch(''); }}
                    className={clsx(
                      'px-3 py-1.5 rounded-lg text-[13px] transition-all text-left',
                      selectedSurah === s.number
                        ? 'bg-emerald-600 text-white font-medium'
                        : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
                    )}
                  >
                    <span className="text-[11px] opacity-50 mr-1">{s.number}</span>{s.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Language */}
            <div className="mb-6">
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2 block">{t(selectedLanguage, 'teacherLang')}</label>
              <div className="flex flex-wrap gap-1.5">
                {LANGUAGES.map((l) => (
                  <button
                    key={l.code}
                    onClick={() => setSelectedLanguage(l.code)}
                    className={clsx(
                      'px-2.5 py-1.5 rounded-lg text-[13px] transition-all flex items-center gap-1.5',
                      selectedLanguage === l.code
                        ? 'bg-emerald-600 text-white font-medium'
                        : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
                    )}
                  >
                    <span className="text-sm">{l.flag}</span>{l.label}
                  </button>
                ))}
              </div>
            </div>

            <button onClick={() => startSession(selectedSurah, 1)} className="btn-primary w-full py-3">
              Start Session
            </button>
            {error && <p className="text-red-500 text-sm text-center mt-3">{error}</p>}
          </div>
        )}

        {/* ── CONNECTING ── */}
        {status === 'connecting' && (
          <div className="flex items-center justify-center min-h-[60vh]">
            <div className="text-center">
              <svg className="animate-spin h-8 w-8 text-emerald-500 mx-auto mb-3" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              <p className="text-sm text-gray-500 dark:text-gray-400">{t(selectedLanguage, 'connecting')}</p>
            </div>
          </div>
        )}

        {/* ── ENDED ── */}
        {status === 'ended' && (
          <div className="max-w-lg mx-auto px-4 py-12">
            <SessionSummaryCard />
          </div>
        )}

        {/* ── ACTIVE: loading ── */}
        {status === 'active' && !(ayahText && messages.length > 0) && (
          <div className="flex items-center justify-center min-h-[60vh]">
            <div className="text-center">
              <div className="w-16 h-16 rounded-2xl bg-emerald-100 dark:bg-emerald-500/20 flex items-center justify-center mx-auto mb-4 animate-pulse text-2xl">🎓</div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">{t(selectedLanguage, 'preparing')}</p>
              <svg className="animate-spin h-5 w-5 text-emerald-500 mx-auto" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            </div>
          </div>
        )}

        {/* ── ACTIVE: session ── */}
        {status === 'active' && ayahText && messages.length > 0 && (() => {
          const total = getTotalAyahs(surah);
          const curName = surahName(surah);

          return (
            <div className="max-w-2xl mx-auto px-4 py-5 flex flex-col gap-4">
              {/* Header */}
              <div className="flex items-center justify-between">
                <div className="min-w-0 flex-1">
                  {renamingActive ? (
                    <input
                      ref={activeRenameRef}
                      value={activeTitle}
                      onChange={(e) => setActiveTitle(e.target.value)}
                      onBlur={() => {
                        if (activeTitle.trim() && activeSessionId) {
                          setPendingRename({ sessionId: activeSessionId, title: activeTitle.trim() });
                        }
                        setRenamingActive(false);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                        if (e.key === 'Escape') setRenamingActive(false);
                      }}
                      className="text-base font-semibold bg-white dark:bg-gray-800 border border-emerald-500 rounded px-2 py-0.5 text-gray-900 dark:text-gray-100 focus:outline-none w-full"
                      maxLength={100}
                    />
                  ) : (
                    <button
                      onClick={() => { setActiveTitle(pendingRename?.sessionId === activeSessionId ? pendingRename.title : curName); setRenamingActive(true); setTimeout(() => activeRenameRef.current?.focus(), 0); }}
                      className="group/rename text-left"
                      title="Click to rename session"
                    >
                      <h1 className="text-base font-semibold text-gray-900 dark:text-white flex items-center gap-1.5">
                        {pendingRename?.sessionId === activeSessionId ? pendingRename.title : curName}
                        <svg className="w-3.5 h-3.5 text-gray-300 dark:text-gray-600 opacity-0 group-hover/rename:opacity-100 transition-opacity" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L6.832 19.82a4.5 4.5 0 01-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 011.13-1.897L16.863 4.487z" />
                        </svg>
                      </h1>
                    </button>
                  )}
                  <p className="text-xs text-gray-500 dark:text-gray-400">Ayah {ayah} of {total}</p>
                </div>
                <button onClick={() => send({ type: 'user_action', action: 'end_session' })} className="text-xs text-red-500 hover:text-red-600 px-2.5 py-1 rounded-lg hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors shrink-0 ml-2">
                  End
                </button>
              </div>

              {/* Transitioning overlay */}
              {transitioning && (
                <div className="flex items-center justify-center py-6 gap-3">
                  <svg className="animate-spin h-5 w-5 text-emerald-500" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  <span className="text-sm text-gray-500 dark:text-gray-400">{t(selectedLanguage, 'preparing')}</span>
                </div>
              )}

              {/* Ayah + reference audio */}
              <div className="card">
                <div className="flex items-center gap-2 mb-3 pb-2.5 border-b border-gray-100 dark:border-gray-800/50">
                  <button
                    onClick={playRef}
                    className={clsx(
                      'w-7 h-7 rounded-full flex items-center justify-center shrink-0 transition-all',
                      refPlaying ? 'bg-amber-500 text-white' : 'bg-emerald-600 hover:bg-emerald-500 text-white'
                    )}
                  >
                    {refPlaying ? (
                      <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24"><rect x="6" y="5" width="4" height="14" rx="1" /><rect x="14" y="5" width="4" height="14" rx="1" /></svg>
                    ) : (
                      <svg className="w-3.5 h-3.5 ml-0.5" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>
                    )}
                  </button>
                  <span className="text-[11px] text-gray-400 dark:text-gray-500">{refPlaying ? t(selectedLanguage, 'playing') : t(selectedLanguage, 'listenFirst')}</span>

                  <div className="ml-auto relative" ref={reciterRef}>
                    <button
                      onClick={() => setReciterOpen(!reciterOpen)}
                      className="flex items-center gap-1 px-2 py-0.5 rounded text-[11px] text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                    >
                      {RECITERS.find((r) => r.id === selectedReciter)?.name}
                      <svg className={clsx('w-3 h-3 transition-transform', reciterOpen && 'rotate-180')} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {reciterOpen && (
                      <div className="absolute right-0 top-full mt-1 z-50 w-40 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg py-0.5">
                        {RECITERS.map((r) => (
                          <button
                            key={r.id}
                            onClick={() => { setSelectedReciter(r.id); setReciterOpen(false); refAudioRef.current?.pause(); setRefPlaying(false); }}
                            className={clsx(
                              'w-full text-left px-3 py-1.5 text-[13px] transition-colors',
                              r.id === selectedReciter ? 'text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-500/10' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                            )}
                          >{r.name}</button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <p className="arabic-text py-3 px-2 text-center leading-loose">{ayahText}</p>
              </div>

              <ConversationLog />

              {/* Floating reference audio bar — always visible */}
              <div className="sticky bottom-0 z-10 bg-white/90 dark:bg-gray-950/90 backdrop-blur border-t border-gray-100 dark:border-gray-800/50 px-3 py-2 -mx-4 flex items-center gap-2">
                <button
                  onClick={playRef}
                  className={clsx(
                    'w-7 h-7 rounded-full flex items-center justify-center shrink-0 transition-all',
                    refPlaying ? 'bg-amber-500 text-white' : 'bg-emerald-600 hover:bg-emerald-500 text-white'
                  )}
                >
                  {refPlaying ? (
                    <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24"><rect x="6" y="5" width="4" height="14" rx="1" /><rect x="14" y="5" width="4" height="14" rx="1" /></svg>
                  ) : (
                    <svg className="w-3.5 h-3.5 ml-0.5" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>
                  )}
                </button>
                <div className="flex-1 min-w-0">
                  <p className="text-[11px] text-gray-500 dark:text-gray-400 truncate font-arabic" dir="rtl">
                    {ayahText?.slice(0, 60)}{ayahText && ayahText.length > 60 ? '...' : ''}
                  </p>
                </div>
                <span className="text-[10px] text-gray-400 dark:text-gray-600 shrink-0">
                  {curName} {ayah}
                </span>
              </div>

              {/* Controls */}
              <div className="flex flex-col items-center gap-3 py-3">
                {isTeacherSpeaking && <TeacherAvatar isSpeaking />}

                {(phase === 'processing' || isSubmitting) && !isTeacherSpeaking && (
                  <div className="flex flex-col items-center gap-2">
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                    >
                      <svg className="h-8 w-8 text-emerald-500" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                    </motion.div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 capitalize">{processingStep || (isSubmitting ? t(selectedLanguage, 'sending') : t(selectedLanguage, 'processing'))}...</p>
                  </div>
                )}

                {phase === 'listening' && !isTeacherSpeaking && !isSubmitting && (
                  <div className="flex flex-col items-center gap-4">
                    {/* Recording orb container */}
                    <div className="relative flex items-center justify-center" style={{ width: 160, height: 160 }}>

                      {/* Ripple rings — radiate outward when recording, scale with volume */}
                      <AnimatePresence>
                        {isRecording && [0, 1, 2].map((i) => (
                          <motion.div
                            key={`ripple-${i}`}
                            className="absolute inset-0 rounded-full border-2 border-red-400/40 dark:border-red-400/30"
                            initial={{ scale: 0.5, opacity: 0.7 }}
                            animate={{
                              scale: [0.5, 1.0 + inputLevel * 0.6],
                              opacity: [0.6, 0],
                            }}
                            transition={{
                              duration: 2,
                              repeat: Infinity,
                              delay: i * 0.6,
                              ease: 'easeOut',
                            }}
                          />
                        ))}
                      </AnimatePresence>

                      {/* Idle breathing glow */}
                      {!isRecording && (
                        <motion.div
                          className="absolute rounded-full bg-emerald-400/20 dark:bg-emerald-400/10"
                          style={{ width: 100, height: 100 }}
                          animate={{ scale: [1, 1.15, 1], opacity: [0.5, 0.8, 0.5] }}
                          transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
                        />
                      )}

                      {/* Circular frequency visualizer — bars arranged in a ring */}
                      {isRecording && (
                        <svg className="absolute" width="160" height="160" viewBox="0 0 160 160">
                          {freqBars.map((v, i) => {
                            const angle = (i / freqBars.length) * Math.PI * 2 - Math.PI / 2;
                            const innerR = 46;
                            const barLen = 4 + v * 22;
                            const x1 = 80 + Math.cos(angle) * innerR;
                            const y1 = 80 + Math.sin(angle) * innerR;
                            const x2 = 80 + Math.cos(angle) * (innerR + barLen);
                            const y2 = 80 + Math.sin(angle) * (innerR + barLen);
                            return (
                              <line
                                key={i}
                                x1={x1} y1={y1} x2={x2} y2={y2}
                                stroke={v > 0.6 ? '#f87171' : v > 0.3 ? '#fb923c' : '#34d399'}
                                strokeWidth="2.5"
                                strokeLinecap="round"
                                opacity={0.4 + v * 0.6}
                              />
                            );
                          })}
                        </svg>
                      )}

                      {/* Main button */}
                      <motion.button
                        onClick={isRecording ? stopRecording : startRecording}
                        className={clsx(
                          'relative z-10 w-20 h-20 rounded-full flex items-center justify-center focus:outline-none',
                          isRecording
                            ? 'bg-gradient-to-br from-red-500 to-red-600 text-white shadow-lg shadow-red-500/30'
                            : 'bg-gradient-to-br from-emerald-500 to-emerald-600 text-white shadow-lg shadow-emerald-500/25 hover:shadow-emerald-500/40'
                        )}
                        whileTap={{ scale: 0.9 }}
                        animate={isRecording ? {
                          scale: [1, 1.04 + inputLevel * 0.08, 1],
                        } : {}}
                        transition={isRecording ? {
                          duration: 0.3,
                          repeat: Infinity,
                          repeatType: 'mirror',
                        } : { type: 'spring', stiffness: 400, damping: 20 }}
                      >
                        <AnimatePresence mode="wait">
                          {isRecording ? (
                            <motion.svg
                              key="stop"
                              className="h-7 w-7"
                              fill="currentColor"
                              viewBox="0 0 24 24"
                              initial={{ scale: 0, rotate: -90 }}
                              animate={{ scale: 1, rotate: 0 }}
                              exit={{ scale: 0, rotate: 90 }}
                              transition={{ type: 'spring', stiffness: 500, damping: 25 }}
                            >
                              <rect x="6" y="6" width="12" height="12" rx="2" />
                            </motion.svg>
                          ) : (
                            <motion.svg
                              key="mic"
                              className="h-7 w-7"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              strokeWidth={2}
                              initial={{ scale: 0, rotate: 90 }}
                              animate={{ scale: 1, rotate: 0 }}
                              exit={{ scale: 0, rotate: -90 }}
                              transition={{ type: 'spring', stiffness: 500, damping: 25 }}
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4M12 15a3 3 0 003-3V5a3 3 0 00-6 0v7a3 3 0 003 3z" />
                            </motion.svg>
                          )}
                        </AnimatePresence>
                      </motion.button>
                    </div>

                    {/* Timer + label */}
                    <AnimatePresence mode="wait">
                      {isRecording ? (
                        <motion.div
                          key="recording-info"
                          initial={{ opacity: 0, y: 8 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -8 }}
                          className="flex flex-col items-center gap-1.5"
                        >
                          <div className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                            <span className="text-sm font-medium tabular-nums text-red-500">
                              {Math.floor(recordingSeconds / 60)}:{String(recordingSeconds % 60).padStart(2, '0')}
                            </span>
                          </div>
                          <p className="text-xs text-gray-400 dark:text-gray-500">{t(selectedLanguage, 'tapToStop')}</p>
                        </motion.div>
                      ) : (
                        <motion.p
                          key="idle-label"
                          initial={{ opacity: 0, y: 8 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -8 }}
                          className="text-xs text-gray-400 dark:text-gray-500"
                        >
                          {t(selectedLanguage, 'tapToRecite')}
                        </motion.p>
                      )}
                    </AnimatePresence>
                  </div>
                )}
              </div>

              {/* Actions */}
              {phase !== 'processing' && !isTeacherSpeaking && !isSubmitting && !isRecording && (
                <div className="flex flex-wrap justify-center gap-2 relative">
                  <button onClick={() => send({ type: 'user_action', action: 'repeat' })} className="btn-secondary text-xs px-3 py-1.5">{t(selectedLanguage, 'repeat')}</button>
                  {ayah < total && (
                    <button onClick={() => { setTransitioning(true); send({ type: 'user_action', action: 'next_ayah' }); }} className="btn-primary text-xs px-3 py-1.5">{t(selectedLanguage, 'nextAyah')}</button>
                  )}
                  <button
                    onClick={() => setShowAyahPicker(!showAyahPicker)}
                    className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-1.5 transition-colors"
                  >
                    {t(selectedLanguage, 'goTo')}
                  </button>

                  {/* Full-screen ayah picker modal */}
                  {showAyahPicker && (
                    <AyahPickerModal
                      surah={surah}
                      currentAyah={ayah}
                      totalAyahs={total}
                      onSelect={(s, a) => {
                        setShowAyahPicker(false);
                        setTransitioning(true);
                        send({ type: 'user_action', action: 'next_ayah', surah: s, ayah: a });
                      }}
                      onClose={() => setShowAyahPicker(false)}
                    />
                  )}
                </div>
              )}

              {error && <p className="text-red-500 text-xs text-center">{error}</p>}
            </div>
          );
        })()}
      </main>
    </div>
  );
}
